import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import { Observable, map, shareReplay } from 'rxjs';
import { UserExportData } from 'app/shared/models/user-export-data.model';
import { AppConstants } from 'app/app.constants';

const HIDDEN_FIELDS = new Set([
  'profile.role',
  'profile._rid',
  'profile._self',
  'profile._etag',
  'profile._attachments',
  'profile._ts',
  'consent._rid',
  'consent._self',
  'consent._etag',
  'consent._attachments',
  'consent._ts',
  'consent.id',
]);

const TIMESTAMP_FIELDS = new Set([
  'profile.validated_at',
  'profile.lastLogin',
  'consent.timestamp',
  'exportedAt',
]);

// Fields holding a language code, shown as the language name.
const LANGUAGE_FIELDS = new Set(['profile.lang']);
const LANGUAGE_NAME_KEY_PREFIX = 'global.';
const LANGUAGE_CODES = new Set<string>(Object.values(AppConstants.LANGUAGES));

const BOOLEAN_FIELDS = new Set([
  'profile.active',
  'consent.analytics',
  'consent.decided',
]);

const SECTIONS: { key: string; fields: string[] }[] = [
  {
    key: 'section-profile',
    fields: [
      'profile.id',
      'profile.name',
      'profile.nationalIdNumber',
      'profile.company',
      'profile.phoneNumber',
      'profile.cellphoneNumber',
      'profile.active',
      'profile.validated_at',
      'profile.lastLogin',
      'profile.lang',
    ],
  },
  {
    key: 'section-address',
    fields: [
      'profile.address.postalCode',
      'profile.address.city',
      'profile.address.stateOrProvince',
      'profile.address.country',
    ],
  },
  {
    key: 'section-consent',
    fields: [
      'consent.analytics',
      'consent.decided',
      'consent.timestamp',
      'consent.policyVersion',
    ],
  },
];

// $agres-primary  hsl(270,1%,31%)   → RGB(79,78,80)
// $agres-description hsl(230,8%,44%) → RGB(103,107,121)
const C_PRIMARY: [number, number, number] = [79, 78, 80];
const C_LABEL: [number, number, number] = [103, 107, 121];
const C_VALUE: [number, number, number] = [30, 30, 30];
const C_SECTION_BG: [number, number, number] = [245, 245, 245];
const C_SEPARATOR: [number, number, number] = [225, 225, 225];
const C_FOOTER: [number, number, number] = [160, 160, 160];

const LOGO_URL = 'assets/images/default/logo-tecomec-brasil.png';
// Logo aspect ratio: 891 × 112 px → 60 mm wide ≈ 7.5 mm tall
const LOGO_W = 60;
const LOGO_H = Math.round((112 / 891) * LOGO_W * 10) / 10;
// Height of the header block (logo, title and timestamp). The logo is centered
// in it, so a logo shorter than the text does not pull the separator up.
const HEADER_H = 11;

@Injectable({ providedIn: 'root' })
export class UserDataPdfService {
  private readonly translateService = inject(TranslateService);
  private readonly http = inject(HttpClient);

  private readonly logo$ = this.http
    .get(LOGO_URL, {
      responseType: 'arraybuffer',
    })
    .pipe(
      map((buf) => {
        const bytes = new Uint8Array(buf);
        let bin = '';
        bytes.forEach((b) => (bin += String.fromCharCode(b)));
        return btoa(bin);
      }),
      shareReplay(1),
    );

  generate(data: UserExportData): Observable<void> {
    return this.logo$.pipe(
      map((logoBase64) => this.buildPdf(data, logoBase64)),
    );
  }

  generateBytes(data: UserExportData): Observable<Uint8Array> {
    return this.logo$.pipe(
      map((logoBase64) => this.buildPdf(data, logoBase64, true) as Uint8Array),
    );
  }

  private buildPdf(data: UserExportData, logoBase64: string): void;
  private buildPdf(
    data: UserExportData,
    logoBase64: string,
    returnBytes: true,
  ): Uint8Array;
  private buildPdf(
    data: UserExportData,
    logoBase64: string,
    returnBytes = false,
  ): void | Uint8Array {
    const lang = this.translateService.currentLang || 'ptBR';
    const t = (key: string) =>
      this.translateService.instant(`pdf-export.${key}`);
    const locale =
      { ptBR: 'pt-BR', en: 'en-US', es: 'es', it: 'it' }[lang] ?? 'pt-BR';

    const flat = this.flattenObjectWithPrefix({
      exportedAt: data.exportedAt,
      profile: data.profile,
      ...(data.consent ? { consent: data.consent } : {}),
    });

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    const labelColW = 58;
    const valueX = margin + labelColW;
    let y = 0;

    const ensureSpace = (needed: number) => {
      if (y + needed > 270) {
        doc.addPage();
        y = 20;
      }
    };

    // ── Header ──────────────────────────────────────────────────
    const headerY = 8;

    doc.addImage(
      logoBase64,
      'PNG',
      margin,
      headerY + (HEADER_H - LOGO_H) / 2,
      LOGO_W,
      LOGO_H,
    );

    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C_PRIMARY);
    doc.text(t('title'), pageW - margin, headerY + 6, { align: 'right' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C_FOOTER);
    doc.text(
      `${t('generated-at')}: ${new Date().toLocaleString(locale)}`,
      pageW - margin,
      headerY + 13,
      { align: 'right' },
    );

    doc.setDrawColor(...C_SEPARATOR);
    doc.setLineWidth(0.3);
    doc.line(
      margin,
      headerY + HEADER_H + 5,
      pageW - margin,
      headerY + HEADER_H + 5,
    );

    y = headerY + HEADER_H + 14;

    // ── Sections ────────────────────────────────────────────────
    for (const section of SECTIONS) {
      const rows = section.fields
        .filter((f) => flat.has(f) && !HIDDEN_FIELDS.has(f))
        .map((f) => ({
          label: t(`fields.${f}`),
          value: this.formatValue(f, flat.get(f), t, locale),
        }))
        .filter((r) => r.value.length > 0 && r.value !== '—');

      if (rows.length === 0) continue;

      ensureSpace(22);

      doc.setFillColor(...C_SECTION_BG);
      doc.rect(margin, y - 4, contentW, 10, 'F');
      doc.setFillColor(...C_PRIMARY);
      doc.rect(margin, y - 4, 3, 10, 'F');

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...C_PRIMARY);
      doc.text(t(section.key).toUpperCase(), margin + 7, y + 2);

      y += 13;

      doc.setFontSize(9);
      for (const row of rows) {
        ensureSpace(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...C_LABEL);
        doc.text(row.label, margin, y);
        doc.setTextColor(...C_VALUE);
        const lines = doc.splitTextToSize(row.value, contentW - labelColW - 4);
        doc.text(lines, valueX, y);
        y += Math.max(9, lines.length * 5 + 4);
      }

      y += 5;
    }

    if (returnBytes) {
      return new Uint8Array(doc.output('arraybuffer'));
    }
    doc.save(t('filename'));
  }

  private formatValue(
    key: string,
    raw: unknown,
    t: (k: string) => string,
    locale: string,
  ): string {
    if (raw == null || raw === '') return '—';
    if (BOOLEAN_FIELDS.has(key)) {
      return raw === true || raw === 'true' ? t('yes') : t('no');
    }
    if (LANGUAGE_FIELDS.has(key)) {
      return this.formatLanguage(String(raw));
    }
    if (TIMESTAMP_FIELDS.has(key)) {
      const ts = Number(raw);
      if (!isNaN(ts) && ts > 1e10) return new Date(ts).toLocaleString(locale);
      const d = new Date(String(raw));
      if (!isNaN(d.getTime())) return d.toLocaleString(locale);
    }
    return String(raw);
  }

  /** Name of a supported language in the UI language; other codes as stored. */
  private formatLanguage(code: string): string {
    if (LANGUAGE_CODES.has(code)) {
      return this.translateService.instant(LANGUAGE_NAME_KEY_PREFIX + code);
    }
    return code;
  }

  private flattenObjectWithPrefix(
    obj: Record<string, unknown>,
    prefix = '',
  ): Map<string, unknown> {
    const result = new Map<string, unknown>();
    for (const [k, v] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${k}` : k;
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        for (const [nk, nv] of this.flattenObjectWithPrefix(
          v as Record<string, unknown>,
          fullKey,
        )) {
          result.set(nk, nv);
        }
      } else {
        result.set(fullKey, Array.isArray(v) ? (v as unknown[]).join(', ') : v);
      }
    }
    return result;
  }
}
